import CommonCss from "nlib/common/common.module.scss";

import Css from "./style.module.scss";

import * as Icons from "@phosphor-icons/react";
import { Button } from "nlib/ui";
import { SplashScreen } from "@capacitor/splash-screen";
import { getAuthErrorDescription } from "selectors/user";
import { getEnvVars } from "selectors/envVars";
import { getTextsData } from "selectors/texts";
import { useDispatch, useSelector } from "react-redux";
import AuthZeroActions from "actions/AuthZeroActions";
import Constants from "const/Constants";
import Platforms from "utils/Platforms";
import Preloader from "nlib/common/Preloader";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Texts from "utils/Texts";
import UserActions from "actions/UserActions";
import classNames from "classnames";

const VERIFY_EMAIL_ERROR_CODE = "verify_email";

const RESEND_VERIFICATION_EMAIL_TIMEOUT = 30000;

const AuthHandler = () => {
  const dispatch = useDispatch();

  const { uiTexts, messages, errors } = useSelector(getTextsData);

  const envVars = useSelector(getEnvVars);

  const errorDescription = useSelector(getAuthErrorDescription);

  const [disableVerifyEmailButton, setDisableVerifyEmailButton] = useState(false);

  const resendVerificationEmailTimeoutRef = useRef(null);

  const errorData = useMemo(() => {
    try {
      return JSON.parse(atob(errorDescription));
    } catch (error) {
      return { code: errorDescription };
    }
  }, [errorDescription]);

  const { code: errorCode, payload: errorPayload } = errorData || {};

  const verifyEmailError = errorCode === VERIFY_EMAIL_ERROR_CODE;

  const handleVerifyEmailButtonClick = useCallback(() => {
    if (disableVerifyEmailButton) return;
    dispatch(UserActions.verifyEmail(errorPayload.userId));
    setDisableVerifyEmailButton(true);
  }, [disableVerifyEmailButton, dispatch, errorPayload]);

  const handleLogoutButtonClick = useCallback(() => {
    dispatch(AuthZeroActions.logoutUser());
  }, [dispatch]);

  useEffect(() => {
    (async() => {
      let checkLoginTimeoutId = null;

      document.body.dataset.auth = "";
      if (errorDescription) {
        if (!verifyEmailError) {
          checkLoginTimeoutId = setTimeout(
            () => dispatch(AuthZeroActions.loginWithRedirect()),
            Constants.LOGIN_PAGE_REFRESH_TIMEOUT
          );
        }
        if (Platforms.checkIsNative()) {
          try {
            await SplashScreen.hide();
          } catch (error) {}
        }
      } else {
        await dispatch(AuthZeroActions.loginWithRedirect(envVars));
      }
      delete document.body.dataset.auth;
      clearTimeout(checkLoginTimeoutId);
    })();
  }, [dispatch, envVars, errorDescription, verifyEmailError]);
  useEffect(() => {
    if (!disableVerifyEmailButton) return undefined;
    resendVerificationEmailTimeoutRef.current = setTimeout(
      () => setDisableVerifyEmailButton(false),
      RESEND_VERIFICATION_EMAIL_TIMEOUT
    );

    return () => {
      clearTimeout(resendVerificationEmailTimeoutRef.current);
      resendVerificationEmailTimeoutRef.current = null;
    };
  }, [disableVerifyEmailButton]);

  return (
    <div className={classNames(Css.authHandler, CommonCss.flexCenter)}>
      <div className={Css.content}>
        {errorCode
          ? <>
            <div className={Css.iconContainer}>
              {verifyEmailError
                ? <>
                  <Icons.EnvelopeOpen weight="thin" className={classNames(Css.icon, Css.semiTransparent)} />
                  <Icons.SealCheck weight="fill" className={classNames(Css.extraIcon, Css.positive)} />
                </>
                : <Icons.Warning weight="thin" className={classNames(Css.icon, Css.warning, Css.semiTransparent)} />}
            </div>
            <div className={Css.title}>
              {verifyEmailError ? messages.verifyEmail : errors.unknown}
            </div>
            <div className={classNames(Css.description, (!verifyEmailError || !errorPayload?.email) && Css.breakWord)}>
              {verifyEmailError && errorPayload?.email
                ? Texts.replaceVars(messages.verifyEmailDescription, { email: errorPayload.email })
                : errorCode}
            </div>
            <div className={Css.buttonContainer}>
              {verifyEmailError && errorPayload?.userId && (
                <Button
                  large
                  dark
                  outline
                  className={Css.resendVerificationEmailButton}
                  disabled={disableVerifyEmailButton}
                  style={{ animationDuration: `${RESEND_VERIFICATION_EMAIL_TIMEOUT}ms` }}
                  onClick={handleVerifyEmailButtonClick}>
                  {uiTexts.resendVerificationEmail}
                </Button>
              )}
              <Button large onClick={handleLogoutButtonClick}>
                {uiTexts.logout}
              </Button>
            </div>
          </>
          : <Preloader fixed />}
      </div>
    </div>
  );
};

export default React.memo(AuthHandler);
